import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { type RootState } from "~/app/store";
import { INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { sendShaftUpdate } from "~/features/configurator/Shaft/api/sendShaftConfiguratorUpdate";
import { sendShaftDataReset } from "~/features/configurator/Shaft/api/sendShaftStepReset";
import { sendShaftResetConfigurator } from "~/features/configurator/Shaft/api/sendShaftResetConfigurator";
import { sendShaftStepChange } from "~/features/configurator/Shaft/api/sendShaftStepChange";
import { sendShaftAddToCart } from "~/features/configurator/Shaft/api/sendShaftAddToCart";
import {
  type ConfiguratorPageDefinition,
  type StepHardcodedData,
} from "~/features/configurator/ConfiguratorPageDefinition";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { InchShaftSummaryData } from "~/features/configurator/Shaft/inch/components/InchShaftSummaryData";
import { AdditionalDataKeys, type Kmat, type LinearConfiguration } from "~/features/configurator/Linear/models";
import * as InchCstics from "~/features/configurator/Shaft/inch/inchCsticConstants";
import {
  chamfer,
  radialThread,
  SHAFT_LENGTH_INCH_MAX,
  SHAFT_LENGTH_METRIC_MAX,
} from "~/features/configurator/Shaft/inch/inchCsticConstants";
import {
  allCsticsHaveValue,
  allCsticsHaveValueInConfiguration,
  getMainKmat,
  hasVisitedStep,
} from "~/features/configurator/Shaft/common/commonShaftHelpers";
import {
  isRadialFullyConfiguredInch,
  showChamferedSection,
  showRadialThreadSection,
  validateChamferSegment,
} from "~/features/configurator/Shaft/shaftConfiguratorHelper";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";
import { convertToSnakeCase } from "~/features/configurator/common/v2/gaHelper";
import { getStep1SummaryData } from "~/features/configurator/Shaft/metric/MetricSummaryDataProvider";
import { getStep2SummaryData } from "~/features/configurator/Shaft/inch/InchSummaryDataProvider";
import {
  type SummaryData,
  type SummaryValueV2,
  transformSummaryDataToV2,
  transformToSelectionData,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import { removeMultipleTags } from "~/common/utils/domUtils";
import { KmatType } from "~/features/configurator/Linear/kmatType";

function prepareSummaryDataName(summaryData: SummaryData) {
  let name = summaryData.name?.toString() ?? "";
  name = removeMultipleTags(name, "p", "strong")!;
  return convertToSnakeCase(name);
}

const HARDCODED_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = HARDCODED_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      const selectionData: SummaryValueV2[] = [];

      [
        { prefix: "", summary: step2Data.length },
        { prefix: "", summary: step2Data.tolerance },
        { prefix: step2Data.left?.name, summary: step2Data.left?.chamfer?.width },
        { prefix: step2Data.left?.name, summary: step2Data.left?.chamfer?.angle },
        { prefix: step2Data.middle?.name, summary: step2Data.middle?.radialThread?.aL },
        { prefix: step2Data.middle?.name, summary: step2Data.middle?.radialThread?.aR },
        { prefix: step2Data.middle?.name, summary: step2Data.middle?.radialThread?.pdt },
        { prefix: step2Data.middle?.name, summary: step2Data.middle?.radialThread?.threadDepth },
        { prefix: step2Data.middle?.name, summary: step2Data.middle?.radialThread?.threadHoleAmount },
        { prefix: step2Data.middle?.name, summary: step2Data.middle?.radialThread?.threadSpacing },
        { prefix: step2Data.right?.name, summary: step2Data.right?.chamfer?.width },
        { prefix: step2Data.right?.name, summary: step2Data.right?.chamfer?.angle },
      ]
        .filter(({ summary }) => summary)
        .forEach(({ prefix, summary }) => {
          let key = [prefix, summary!.name].filter(Boolean).join(" ");
          key = removeMultipleTags(key, "p", "strong")!;
          key = convertToSnakeCase(key);
          selectionData.push({
            ...summary,
            name: `${prefix} - ${summary!.name}`,
            gaName: key,
            value: summary!.value,
          });
        });

      selectionData.forEach((data) => {
        data.value = removeMultipleTags(data.value, "p", "span", "strong")!;
        data.name = removeMultipleTags(data.name, "p", "span", "strong")!;
      });

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: selectionData,
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = HARDCODED_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];
export const IMPERIAL_SHAFT_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.InchShaft,
  productCode: INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.shaft.title.inch",
  },
  apiCalls: {
    update: sendShaftUpdate,
    reset: sendShaftResetConfigurator,
    stepChange: sendShaftStepChange,
    stepReset: sendShaftDataReset,
    addToCart: sendShaftAddToCart,
  },
  summary: InchShaftSummaryData,
  summaryParams: () => <div />,
  isAddToCartEnabled: (state: RootState) => {
    const visitedStep2 = hasVisitedStep(2)(state);
    const hasAllCstics = allCsticsHaveValue(
      KmatType.Main,
      InchCstics.SHAFT_LENGTH_METRIC,
      InchCstics.SHAFT_LENGTH_INCH,
      InchCstics.shaftTolerance.value,
      InchCstics.SHAFT_MACHINING_LEFT,
      InchCstics.SHAFT_MACHINING_MIDDLE,
      InchCstics.SHAFT_MACHINING_RIGHT,
    )(state);

    return visitedStep2 && hasAllCstics;
  },
  isSaveEnabled: (state: RootState) => {
    const visitedStep2 = hasVisitedStep(2)(state);

    const hasLength = allCsticsHaveValue(
      KmatType.Main,
      InchCstics.SHAFT_LENGTH_METRIC,
      InchCstics.SHAFT_LENGTH_INCH,
    )(state);

    return visitedStep2 && hasLength;
  },
  stepHardcodedData: HARDCODED_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    const avoidableCstics = [
      InchCstics.SHAFT_LENGTH_METRIC,
      InchCstics.SHAFT_LENGTH_INCH,
      InchCstics.SHAFT_MACHINING_LEFT,
      InchCstics.SHAFT_MACHINING_MIDDLE,
      InchCstics.SHAFT_MACHINING_RIGHT,
      InchCstics.chamfer.left.width.sf,
      InchCstics.chamfer.right.width.sf,
      SHAFT_LENGTH_INCH_MAX,
      SHAFT_LENGTH_METRIC_MAX,
    ];
    return avoidableCstics.includes(csticName);
  },
  generateDesignation: (kmats: Dict<Kmat[]>) => {
    const kmat = kmats[KmatType.Main]?.[0];

    // Create the designation using `{series}{diameter}-{material}-{left}/{middle}/{right}`;
    // Exception - skip material if it's standard
    const materialCstic = kmat.cstics[InchCstics.MATERIAL];
    const series = kmat.cstics[InchCstics.SERIES]?.value;
    const diameter = kmat.cstics[InchCstics.DIAMETER]?.value;
    const material = materialCstic?.value;
    const left = kmat.cstics[InchCstics.SHAFT_MACHINING_LEFT]?.value;
    const middle = kmat.cstics[InchCstics.SHAFT_MACHINING_MIDDLE]?.value;
    const right = kmat.cstics[InchCstics.SHAFT_MACHINING_RIGHT]?.value;

    const isStandardMaterial = materialCstic?.values.find((v) => v.value === material)?.standard;

    let designation = "";
    const STEPS = [
      [
        () => !!series && !!diameter,
        (currentDesignation: string) => currentDesignation + series! + diameter!.split(".")[0],
      ],
      [
        () => !!material,
        (currentDesignation: string) => currentDesignation + (isStandardMaterial ? "" : "-" + material!),
      ],
      [
        () => !!left || !!middle || !!right,
        (currentDesignation: string) => currentDesignation + "-" + [left, middle, right].filter(Boolean).join("/"),
      ],
    ] as const;

    for (let i = 0; i < STEPS.length; i += 1) {
      if (STEPS[i][0]()) {
        designation = STEPS[i][1](designation);
      } else {
        break; // All following steps are also not valid
      }
    }
    return designation || "N/A";
  },
  postDefinitionProcessor: (model) => {
    const kmat = getMainKmat({
      kmats: model.kmats,
    });

    model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.forEach((group) => {
      group.tiles.forEach((tile) => {
        delete tile.csticsToEnable[chamfer.left.angle];
        delete tile.csticsToEnable[chamfer.right.angle];
        delete tile.csticsToEnable[radialThread.aLaREnabler];
      });
    });
    const leftDeburred = model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.[0]?.tiles.find(
      (tile) => tile.cstics[InchCstics.SHAFT_MACHINING_LEFT] === "005",
    );
    const middleRadialThreadedHoles = model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.[1]?.tiles.find(
      (tile) => tile.cstics[InchCstics.SHAFT_MACHINING_MIDDLE] === "980",
    );
    const rightDeburred = model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.[2]?.tiles.find(
      (tile) => tile.cstics[InchCstics.SHAFT_MACHINING_RIGHT] === "005",
    );

    if (leftDeburred) {
      leftDeburred.csticsToSetOnly = leftDeburred.csticsToSetOnly ?? {};
      leftDeburred.csticsToSetOnly[chamfer.left.angle] = "NF30";
    }
    if (middleRadialThreadedHoles) {
      middleRadialThreadedHoles.csticsToSetOnly = middleRadialThreadedHoles.csticsToSetOnly ?? {};
      middleRadialThreadedHoles.csticsToSetOnly[radialThread.aLaREnabler] = "S";
    }
    if (rightDeburred) {
      rightDeburred.csticsToSetOnly = rightDeburred.csticsToSetOnly ?? {};
      rightDeburred.csticsToSetOnly[chamfer.right.angle] = "NF30";
    }

    const substep = model.substeps[SubstepType.Diameter];
    if (substep && substep.additionalData) {
      substep.additionalData[AdditionalDataKeys.TitleFontSize] = "24px";
    }

    const lengthMetric = kmat.cstics[InchCstics.SHAFT_LENGTH_METRIC];
    const lengthInch = kmat.cstics[InchCstics.SHAFT_LENGTH_INCH];
    [lengthMetric, lengthInch].forEach((cstic) => {
      if (cstic && cstic.value) {
        cstic.value = (+cstic.value).toFixed(4);
      }
    });
    if (lengthMetric) {
      lengthMetric.label = "configurator.shaft.step.2.substep.1.title.metric";
    }
    if (lengthInch) {
      lengthInch.label = "configurator.shaft.step.2.substep.1.title.inch";
    }

    const page2 = model.steps[1];
    if (page2 && "defaultValues" in page2) {
      const defaultValues = page2.defaultValues as Record<string, string | null>;
      // Shaft Length
      defaultValues[InchCstics.SHAFT_LENGTH_METRIC] = null;
      defaultValues[InchCstics.SHAFT_LENGTH_INCH] = null;
      // Shaft Machining
      defaultValues[InchCstics.SHAFT_MACHINING_LEFT] = null;
      defaultValues[InchCstics.SHAFT_MACHINING_MIDDLE] = null;
      defaultValues[InchCstics.SHAFT_MACHINING_RIGHT] = null;
      // Machining extra fierlds
      defaultValues[chamfer.left.angle] = null;
      defaultValues[chamfer.left.width.sf] = null;
      defaultValues[chamfer.right.angle] = null;
      defaultValues[chamfer.right.width.sf] = null;
      defaultValues[radialThread.aL.inch.value] = null;
      defaultValues[radialThread.aR.inch.value] = null;
      defaultValues[radialThread.aL.metric.value] = null;
      defaultValues[radialThread.aR.metric.value] = null;
      defaultValues[radialThread.aLaREnabler] = null;
    }

    model.allowedSteps = calculateAllowedSteps(model);

    // SCHAEFF-31580 - Manually validate that the length is not too much, and create a conflict if it exceeds max
    [
      [InchCstics.SHAFT_LENGTH_METRIC, InchCstics.SHAFT_LENGTH_METRIC_MAX],
      [InchCstics.SHAFT_LENGTH_INCH, InchCstics.SHAFT_LENGTH_INCH_MAX],
    ].forEach(([lengthCsticName, maxLengthCsticName]) => {
      const lengthValue = kmat.cstics[lengthCsticName]?.value;
      const maxLengthValue = kmat.cstics[maxLengthCsticName]?.value;
      if (lengthValue && maxLengthValue) {
        const length = +lengthValue;
        const maxLength = +maxLengthValue;
        if (length > maxLength && "conflictedCstics" in model) {
          model.conflictedCstics.push({
            id: "Invalid length",
            name: "Invalid length",
            cstics: [
              {
                name: lengthCsticName,
                key: lengthCsticName,
              },
              {
                name: maxLengthCsticName,
                key: maxLengthCsticName,
              },
            ],
          });
        }
      }
    });

    return model;
  },
  gaConfiguratorName: "shaft-inch",
} as const;

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const allowedSteps = [
    [true, false, false],
    [true, true, false],
    [true, true, true],
  ];

  const kmat = getMainKmat({ kmats: model.kmats });

  const canVisitStep2 = allCsticsHaveValueInConfiguration(
    kmat,
    InchCstics.SERIES,
    InchCstics.DIAMETER,
    InchCstics.MATERIAL,
    InchCstics.COATING,
    InchCstics.TOLERANCE,
  );
  allowedSteps[0][1] = canVisitStep2;

  let canVisitStep3 = allCsticsHaveValueInConfiguration(
    kmat,
    InchCstics.SHAFT_LENGTH_METRIC,
    InchCstics.SHAFT_LENGTH_INCH,
    InchCstics.shaftTolerance.value,
    InchCstics.SHAFT_MACHINING_LEFT,
    InchCstics.SHAFT_MACHINING_MIDDLE,
    InchCstics.SHAFT_MACHINING_RIGHT,
  );

  const leftMachining = kmat.cstics[InchCstics.SHAFT_MACHINING_LEFT]?.value;
  const middleMachining = kmat.cstics[InchCstics.SHAFT_MACHINING_MIDDLE]?.value;
  const rightMachining = kmat.cstics[InchCstics.SHAFT_MACHINING_RIGHT]?.value;

  let conflictedCstics: string[];
  if ("conflictedCstics" in model) {
    conflictedCstics = model.conflictedCstics.flatMap((c) => c.cstics.map((c) => c.name));
  } else {
    conflictedCstics = [];
  }

  const validationCtx = { kmat, conflictedCstics, mode: "full" } as const;

  if (showChamferedSection(leftMachining)) {
    canVisitStep3 &&= validateChamferSegment(InchCstics.chamfer.left, validationCtx).length === 0;
  }
  if (showChamferedSection(rightMachining)) {
    canVisitStep3 &&= validateChamferSegment(InchCstics.chamfer.right, validationCtx).length === 0;
  }
  if (showRadialThreadSection(middleMachining)) {
    canVisitStep3 &&= isRadialFullyConfiguredInch(kmat);
  }
  allowedSteps[1][2] = canVisitStep3;

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(IMPERIAL_SHAFT_DEFINITION);
